import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Person,
  Email,
  Phone,
  Business,
  Work,
  CalendarToday,
  AttachMoney,
  Schedule,
  CheckCircle,
  Cancel,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { fetchAttendanceSummary } from '../../store/slices/attendanceSlice'

interface Employee {
  id?: number
  employee_number: string
  first_name: string
  last_name: string
  email: string
  phone: string
  department: string
  position: string
  hire_date: string
  status: string
  hourly_rate?: number
  salary?: number
  created_at?: string
  updated_at?: string
}

interface EmployeeDetailsDialogProps {
  open: boolean
  onClose: () => void
  employee: Employee | null
  onEdit?: () => void
}

const DetailRow: React.FC<{
  icon: React.ReactNode
  label: string
  value: string | number | undefined
}> = ({ icon, label, value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      mb: { xs: 2, sm: 3 },
      p: { xs: 2, sm: 3 },
      backgroundColor: 'background.default',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 1,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: 2,
        borderColor: 'primary.light',
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: { xs: 40, sm: 48 },
        height: { xs: 40, sm: 48 },
        borderRadius: 3,
        backgroundColor: 'primary.light',
        mr: { xs: 2, sm: 3 },
        color: 'primary.main',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontWeight: 500,
          mb: 0.5,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: 'text.primary',
          fontSize: { xs: '0.9rem', sm: '1rem' },
          wordBreak: 'break-word',
        }}
      >
        {value || 'Not specified'}
      </Typography>
    </Box>
  </Box>
)

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'warning'
    case 'terminated':
      return 'error'
    default:
      return 'default'
  }
}

export const EmployeeDetailsDialog: React.FC<EmployeeDetailsDialogProps> = ({
  open,
  onClose,
  employee,
  onEdit,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useAppDispatch()
  const { summary: attendanceSummary, isLoading: attendanceLoading } = useAppSelector((state) => state.attendance)

  useEffect(() => {
    if (employee?.id && open) {
      // Fetch attendance summary for the current month
      const now = new Date()
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      
      dispatch(fetchAttendanceSummary({
        employeeId: employee.id,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      }))
    }
  }, [employee?.id, open, dispatch])

  if (!employee) return null

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return undefined
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4 },
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 4,
          overflow: 'hidden',
          maxHeight: { xs: '100vh', sm: '90vh' },
          m: { xs: 0, sm: 2 },
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: 'background.default',
          color: 'text.primary',
          fontWeight: 600,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 4 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: 'text.primary',
            }}
          >
            Employee Details
          </Typography>
          <Chip
            label={employee.status}
            color={getStatusColor(employee.status) as any}
            size="medium"
            sx={{
              backgroundColor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              fontWeight: 500,
              '& .MuiChip-label': {
                px: 2,
                py: 0.5,
              },
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: 'background.paper',
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box sx={{ mt: 1 }}>
          {/* Personal Information */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Personal Information
          </Typography>
          <DetailRow
            icon={<Person color="primary" />}
            label="Employee Number"
            value={employee.employee_number}
          />
          <DetailRow
            icon={<Person color="primary" />}
            label="Full Name"
            value={`${employee.first_name} ${employee.last_name}`}
          />
          <DetailRow
            icon={<Email color="primary" />}
            label="Email"
            value={employee.email}
          />
          <DetailRow
            icon={<Phone color="primary" />}
            label="Phone"
            value={employee.phone}
          />

          <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: 'divider' }} />

          {/* Employment Information */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Employment Information
          </Typography>
          <DetailRow
            icon={<Business color="primary" />}
            label="Department"
            value={employee.department}
          />
          <DetailRow
            icon={<Work color="primary" />}
            label="Position"
            value={employee.position}
          />
          <DetailRow
            icon={<CalendarToday color="primary" />}
            label="Hire Date"
            value={formatDate(employee.hire_date)}
          />

          <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: 'divider' }} />

          {/* Compensation Information */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Compensation
          </Typography>
          {employee.hourly_rate && (
            <DetailRow
              icon={<AttachMoney color="primary" />}
              label="Hourly Rate"
              value={formatCurrency(employee.hourly_rate)}
            />
          )}
          {employee.salary && (
            <DetailRow
              icon={<AttachMoney color="primary" />}
              label="Annual Salary"
              value={formatCurrency(employee.salary)}
            />
          )}

          {(employee.created_at || employee.updated_at) && (
            <>
              <Divider sx={{ my: 4, borderColor: 'outlineVariant' }} />
              <Typography variant="h6" gutterBottom sx={{ color: 'onSurface', fontWeight: 500, mb: 3 }}>
                System Information
              </Typography>
              {employee.created_at && (
                <DetailRow
                  icon={<CalendarToday color="primary" />}
                  label="Created"
                  value={formatDate(employee.created_at)}
                />
              )}
              {employee.updated_at && (
                <DetailRow
                  icon={<CalendarToday color="primary" />}
                  label="Last Updated"
                  value={formatDate(employee.updated_at)}
                />
              )}
            </>
          )}

          {/* Attendance Information */}
          <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: 'divider' }} />
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Current Month Attendance
          </Typography>
          
          {attendanceLoading ? (
            <Box sx={{ mb: 3 }}>
              <LinearProgress sx={{ borderRadius: 1 }} />
              <Typography variant="body2" color="onSurfaceVariant" sx={{ mt: 1, textAlign: 'center' }}>
                Loading attendance data...
              </Typography>
            </Box>
          ) : attendanceSummary ? (
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{
                  backgroundColor: 'success.light',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'success.main',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                      gap: { xs: 0.5, sm: 1 }
                    }}>
                      <CheckCircle sx={{
                        color: 'success.main',
                        fontSize: { xs: 20, sm: 24 }
                      }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'success.main',
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        {attendanceSummary.present_days}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Present Days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{
                  backgroundColor: 'error.light',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'error.main',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                      gap: { xs: 0.5, sm: 1 }
                    }}>
                      <Cancel sx={{
                        color: 'error.main',
                        fontSize: { xs: 20, sm: 24 }
                      }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'error.main',
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        {attendanceSummary.absent_days}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'error.main',
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Absent Days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{
                  backgroundColor: 'warning.light',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'warning.main',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                      gap: { xs: 0.5, sm: 1 }
                    }}>
                      <Schedule sx={{
                        color: 'warning.main',
                        fontSize: { xs: 20, sm: 24 }
                      }} />
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'warning.main',
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        {attendanceSummary.total_hours_worked?.toFixed(1) || '0.0'}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'warning.main',
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Total Hours
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={6} sm={6} md={3}>
                <Card sx={{
                  backgroundColor: 'primary.light',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        mb: 1,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                      }}
                    >
                      {attendanceSummary.attendance_percentage?.toFixed(1) || '0.0'}%
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Attendance Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: 'surfaceVariant',
              borderRadius: 2,
              mb: 3
            }}>
              <Schedule sx={{ fontSize: 48, color: 'onSurfaceVariant', mb: 1 }} />
              <Typography variant="body1" color="onSurfaceVariant">
                No attendance data available for this month
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: 'background.default',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: { xs: 2, sm: 3, md: 4 },
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={false}
          sx={{
            borderRadius: 3,
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 500,
            px: { xs: 3, sm: 4 },
            py: { xs: 1.25, sm: 1.5 },
            textTransform: 'none',
            fontSize: '1rem',
            minWidth: { xs: '100%', sm: 120 },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'text.primary',
            },
          }}
        >
          Close
        </Button>
        {onEdit && (
          <Button
            onClick={onEdit}
            variant="contained"
            fullWidth={false}
            sx={{
              borderRadius: 3,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 500,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.25, sm: 1.5 },
              textTransform: 'none',
              fontSize: '1rem',
              minWidth: { xs: '100%', sm: 140 },
              boxShadow: 2,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: 4,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                boxShadow: 1,
                transform: 'translateY(0)',
              },
            }}
          >
            Edit Employee
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default EmployeeDetailsDialog
